import { useState } from "react"
import { TbChartBar } from "react-icons/tb"
import { BaseTable, Modal, MultipleChart, MultipleChartData, TableHeader } from "components"
import { convertNumber, useToggle } from "utils"
import { ChartOptions } from "chart.js"
import { SalesYearlyGroup, SalesYearlyItem } from "../types/SalesYearlyType"

type Props = {
  list: SalesYearlyGroup[]
  isFilterReady: boolean
}

export default function SalesYearlyTable({ list, isFilterReady }: Props) {
  if (!list.length) return null

  const years = list[0].items[0]?.data.map((d) => d.year) ?? []

  if (!years.length) return null

  return (
    <div className="overflow-x-auto mt-6">
      <div className="max-h-[600px] overflow-y-auto">
        <BaseTable className="table table-bordered w-full">
          <TableHeader>
            <tr className="bg-black text-white text-xs">
              <th rowSpan={2} className="text-left">KEY DESCRIPTION</th>
              {years.map((year) => (
                <th key={year} colSpan={2} className="text-center min-w-[100px]">
                  {year}
                </th>
              ))}
              <th rowSpan={2} className="text-center">ACTION</th>
            </tr>
            <tr className="bg-black text-white text-xs">
              {years.map((year) => (
                <>
                  <th key={`budget-${year}`} className="text-center">BUDGET</th>
                  <th key={`real-${year}`} className="text-center">REAL</th>
                </>
              ))}
            </tr>
          </TableHeader>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={years.length * 2 + 2} className="text-center">No data</td>
              </tr>
            ) : (
              list.map((group) => (
                <>
                  {/* Category row */}
                  <tr key={`cat-${group.code_budget}`} className="bg-base-300 font-semibold">
                    <td colSpan={years.length * 2 + 2}>{group.category}</td>
                  </tr>

                  {group.items.map((item) => {
                    const rowKey = `${group.code_budget}_${item.location_id != null ? String(item.location_id) : item.location_name}`
                    return (
                      <tr key={rowKey}>
                        <td className="font-medium !align-middle ps-10 min-w-[200px]">
                          {item.location_name}
                        </td>
                        {years.map((year) => {
                          const yearData = item.data.find((d) => d.year === year)
                          return (
                            <>
                              <td key={`budget-${rowKey}-${year}`} className="text-right !align-middle">
                                {yearData?.budget ? convertNumber(Number(yearData.budget)).intoCurrency : ""}
                              </td>
                              <td key={`real-${rowKey}-${year}`} className="text-right !align-middle">
                                {yearData?.actual ? convertNumber(Number(yearData.actual)).intoCurrency : ""}
                              </td>
                            </>
                          )
                        })}
                        <td className="text-center !align-middle">
                          <ChartSectionYearly
                            title={`${group.category} — ${item.location_name}`}
                            item={item}
                            disabled={!isFilterReady}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </>
              ))
            )}
          </tbody>
        </BaseTable>
      </div>
    </div>
  )
}

function ChartSectionYearly({ title, item, disabled }: { title: string; item: SalesYearlyItem; disabled?: boolean }) {
  const { isActive, toggle } = useToggle(false)
  const [chartType, setChartType] = useState<"line" | "bar">("line")

  const labels = item.data.map((d) => String(d.year))
  const actualValues = item.data.map((d) => d.actual != null ? Number(d.actual) : null)

  const data: MultipleChartData = {
    labels,
    datasets: [
      {
        label: "Actual",
        data: actualValues,
        borderColor: chartType === "line" ? "#3B82F6" : "rgb(59, 130, 246, 0.5)",
        borderWidth: 2,
        spanGaps: false
      }
    ]
  }

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 25 },
    scales: chartType === "bar" ? {
      x: {
        position: "center",
        ticks: { display: false },
        grid: { display: false },
        border: { display: false }
      },
      y: {
        grid: {
          color: (ctx) => ctx.tick.value === 0 ? "rgb(255,193,7)" : "rgba(0,0,0,0)"
        },
        border: { display: false }
      }
    } : {
      x: {
        ticks: { display: true },
        grid: { display: false },
        border: { display: false }
      },
      y: {
        grid: {
          color: (ctx) => ctx.tick.value === 0 ? "rgb(255,193,7)" : "rgba(0,0,0,0)"
        },
        border: { display: false }
      }
    },
    plugins: {
      legend: { display: false },
      datalabels: chartType === "bar" ? {
        clip: false,
        labels: {
          year: {
            color: "#666",
            formatter: (_, context) => labels[context.dataIndex],
            anchor: (context) => (context.dataset.data[context.dataIndex] as number) > 0 ? "start" : "end",
            align: (context) => (context.dataset.data[context.dataIndex] as number) > 0 ? "bottom" : "top"
          }
        }
      } : {
        clip: false,
        labels: {
          value: {
            color: "#666",
            display: (ctx) => ctx.dataset.data[ctx.dataIndex] !== 0,
            font: { weight: "bold" },
            formatter: (value: number) => convertNumber(value).intoCurrency,
            anchor: (context) => (context.dataset.data[context.dataIndex] as number) > 0 ? "start" : "end",
            align: (context) => (context.dataset.data[context.dataIndex] as number) > 0 ? "top" : "bottom"
          }
        }
      }
    }
  }

  return (
    <>
      <button onClick={toggle} disabled={disabled} className="btn btn-xs btn-warning text-white">
        <TbChartBar size={16} />
      </button>

      {isActive && (
        <Modal isOpen title={`${title.toUpperCase()} CHART`} closeModal={toggle} isdarktitle>
          <Modal.Body>
            <div className="flex justify-end mb-2">
              <select
                className="select select-sm select-bordered"
                value={chartType}
                onChange={(e) => setChartType(e.target.value as "line" | "bar")}
              >
                <option value="line">Line</option>
                <option value="bar">Bar</option>
              </select>
            </div>
            <div style={{ height: 450 }}>
              <MultipleChart
                className="min-h-[450px]"
                chartType={chartType as "bar"}
                data={data}
                options={options}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className="border-2 border-gray-400 min-h-[300px] rounded-lg p-4">
                <h2 className="font-bold mb-3">FORMULA</h2>
                <div className="text-sm space-y-2">
                  <div dangerouslySetInnerHTML={{ __html: item.formula ?? "" }} />
                </div>
              </div>

              <div className="border-2 border-gray-400 min-h-[300px] rounded-lg p-4">
                <h2 className="font-bold mb-3">AI ANALYTIC</h2>
                <div className="text-sm text-gray-600">
                </div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}
